import { useEffect, useState } from "react";
import { Button, Flex, Form, Input, InputNumber, Switch } from "antd";
import { MasterDataServices } from "../../../../services/master-data";
import { useMessageApi } from "../../../../context/MessageContext";
import { IMasterData, IMasterDataInput, IMasterDataResponse } from "../../../../interfaces/master-data";

interface CreateAndEditCurrencyProps {
  onClose: (okTriggered?: boolean) => void;
  masterData: IMasterData | undefined;
}

interface ICurrencyInput {
  type: string;
  code: string;
  label: string;
  tolerancePercent: number;
  toleranceAbsolute: number;
  exchangeRate: number;
  isActive: boolean;
}

const CreateAndEditCurrency: React.FC<CreateAndEditCurrencyProps> = ({
  onClose,
  masterData,
}) => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEdit = !!masterData;

  useEffect(() => {
    if (masterData) {
      form.setFieldsValue({
        type: masterData.type,
        code: masterData.code,
        label: masterData.label,
        toleranceAbsolute: masterData.value?.toleranceAbsolute,
        tolerancePercent: masterData.value?.tolerancePercent,
        exchangeRate: masterData.value?.exchangeRate,
        isActive: masterData.isActive,
      } as ICurrencyInput);
    }
  }, [form, masterData]);

  const onFinish = async (values: ICurrencyInput) => {
    try {
      setIsSubmitting(true);
      const payload: IMasterDataInput = {
        type: masterData?.type ?? 'CURRENCY',
        code: values.code?.trim(),
        label: values.label?.trim(),
        value: {
          toleranceAbsolute: values.toleranceAbsolute,
          tolerancePercent: values.tolerancePercent,
          exchangeRate: values.exchangeRate,
        },
        isActive: values.isActive,
      };
      const response: IMasterDataResponse = isEdit
        ? await MasterDataServices.update(String(masterData.id), payload)
        : await MasterDataServices.create(payload);

      if (response.success) {
        messageApi.success(isEdit ? "Update currency successfully" : "New currency created successfully");
        onClose(true);
      } else {
        messageApi.error(response.message);
      }
    } catch (error: any) {
      messageApi.error(error?.message || "Failed to save currency");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ isDefault: false }}
    >
      <Form.Item
        label="Currency Code"
        name="code"
        rules={[
          { required: true, message: "Complete this required field." },
          { max: 10, message: "Max length 10 characters" },
        ]}
      >
        <Input placeholder="Enter Currency Code" disabled={isEdit}/>
      </Form.Item>

      <Form.Item
        label="Currency Name"
        name="label"
        rules={[{ required: true, message: "Complete this required field." }, { max: 100, message: "Max length 100 characters" },]}
      >
        <Input placeholder="Enter Currency Name" />
      </Form.Item>

      <Form.Item
        label="Tolerance (%)"
        name="tolerancePercent"
        rules={[{ required: true, message: "Complete this required field." }]}
      >
        <InputNumber placeholder="Enter Tolerance(%)" style={{ width: "100%" }} min={0} precision={2} />
      </Form.Item>
      <Form.Item
        label="Tolerance (Absolute)"
        name="toleranceAbsolute"
        rules={[{ required: true, message: "Complete this required field." }]}
      >
        <InputNumber placeholder="Enter Tolerance (Absolute)" style={{ width: "100%" }} min={0} precision={2} />
      </Form.Item>

      <Form.Item
        label="Exchange Rate"
        name="exchangeRate"
        rules={[{ required: true, message: "Complete this required field." }]}
      >
        <InputNumber placeholder="Enter Exchange Rate" style={{ width: "100%" }} min={0} precision={2} />
      </Form.Item>

      <Form.Item label="Active" name="isActive" valuePropName="checked">
        <Switch />
      </Form.Item>

      <Flex justify="end" gap={16}>
        <Button onClick={() => onClose()} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" loading={isSubmitting}>
          {isEdit ? "Update" : "Create"}
        </Button>
      </Flex>
    </Form>
  );
};

export default CreateAndEditCurrency;
